import { Card, FormHelperText } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

import nanoid from "../../lib/nanoid";
import FileDropZone from "./FileDropZone";
import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";
import { IFileUploaderRes, UploadFileResponse } from "../../commons/interfaces";
import { FileAccessMode, Mode } from "../../constants/enums";
import Box from "../Box";
import Typography from "../Typography";

export interface IHiddenActionImageUploader {
    view?: boolean;
    replace?: boolean;
    remove?: boolean;
}

interface IProps {
    folderPath: string;
    optimize?: boolean;
    accessMode?: FileAccessMode;
    width?: number | string;
    containerClassName?: string;
    multiple?: boolean;
    uploadsPerTime?: number;
    defaultValue?: IFileUploaderRes[];
    label?: string;
    title?: string;
    mode?: Mode;
    maximumFiles?: number;
    removeShadow?: boolean;
    required?: boolean;
    errorMessage?: string;
    onChange?: (files: IFileUploaderRes[]) => void;
    finalOnChange?: (files: IFileUploaderRes[]) => void;
    hiddenAction?: IHiddenActionImageUploader;
}

export interface IFileItem {
    renderKeyId: string;
    startUpload?: boolean;
    fileBinary?: File;
    fileUrl?: string;
    fileId?: string;
    fileAccessUrl?: string;
    id?: string;
}

enum TabIndex {
    UploadFromLocal = 0,
}

const MultipleImageUploader: React.FC<IProps> = (props: IProps) => {
    const strings = useStrings();
    const uploadsPerTime = props.uploadsPerTime || 1;
    const fileListContainerRef = useRef<any>();
    const tabIndexRef = useRef<TabIndex>(TabIndex.UploadFromLocal);

    const maximumFiles = props.maximumFiles;
    const errorMessage = props.errorMessage;
    // Handle changing file to upload
    const singleChangeFileUploadRef = useRef<any>();
    const changeFileRenderKeyId = useRef<string>();

    // Used for props.onChange()
    const fileListRef = useRef<IFileItem[]>([]);
    const uploadingFiles = useRef<IFileItem[]>([]);

    // Used for rendering
    const [fileList, setFileList] = useState<IFileItem[]>(fileListRef.current);

    const handleUploadFile = (fileBinaryList?: File[], fileUrl?: string) => {
        if (!Helpers.isNullOrEmpty(fileBinaryList) && fileBinaryList.length > 0) {
            fileListRef.current.push(
                ...fileBinaryList.map((fileBinary) => ({
                    renderKeyId: nanoid(),
                    startUpload: false,
                    fileBinary,
                }))
            );

            let uploads = 1;
            for (let file of fileListRef.current) {
                if (file.startUpload === false && !file.fileId && uploads <= uploadsPerTime) {
                    uploadingFiles.current.push(file);
                    file.startUpload = true;
                    uploads += 1;
                }
            }
        }

        if (!Helpers.isNullOrEmpty(fileUrl)) {
            fileListRef.current.push({
                renderKeyId: nanoid(),
                fileUrl,
            });

            props.onChange &&
                props.onChange(
                    fileListRef.current.map((file) => ({
                        fileId: file.fileId,
                        fileUrl: file.fileUrl,
                    }))
                );
        }

        setFileList([...fileListRef.current]);
    };

    const handleAddFile = (fileBinaryList?: File[], fileUrl?: string) => {
        if (props.multiple === false) {
            fileListRef.current = [];
        }

        if (Helpers.isNullOrEmpty(maximumFiles)) return handleUploadFile(fileBinaryList, fileUrl);
        if (fileListRef.current?.length >= maximumFiles) return;
        if (fileListRef.current?.length + fileBinaryList.length >= maximumFiles) {
            const newFiles = [...fileBinaryList];
            newFiles.splice(maximumFiles - fileListRef?.current?.length);
            return handleUploadFile(newFiles, fileUrl);
        }
        return handleUploadFile(fileBinaryList, fileUrl);
    };

    useEffect(() => {
        // Scroll preview file container to bottom
        if (fileListContainerRef.current) {
            fileListContainerRef.current.scrollTop = fileListContainerRef.current.scrollHeight;
        }
    }, [fileList]);

    useEffect(() => {
        if (!Helpers.isNullOrEmpty(props.defaultValue) && props.defaultValue.length > 0 && fileListRef.current.length === 0) {
            // Avoid duplicate fileId
            let fileIdLookup: { [fileId: string]: boolean } = {};
            let defaultValue = props.multiple === false ? [props.defaultValue[0]] : props.defaultValue;

            for (let file of defaultValue) {
                if (
                    file.fileId &&
                    (file.fileId !== "0" || (file.fileId === "0" && !Helpers.isNullOrEmpty(file.fileUrl))) &&
                    !fileIdLookup[file.fileId + file.fileUrl]
                ) {
                    fileListRef.current.push({
                        renderKeyId: nanoid(),
                        fileId: file.fileId,
                        fileUrl: file.fileUrl,
                        fileAccessUrl: file.fileAccessUrl,
                        id: file.id,
                    });
                    fileIdLookup[file.fileId + file.fileUrl] = true;
                }
            }
            setFileList([...fileListRef.current]);
        }
    }, [props.defaultValue]);

    const propsOnChangeCallback = () => {
        const files = fileListRef.current
            .filter((file) => !Helpers.isNullOrEmpty(file.fileId) || !Helpers.isNullOrEmpty(file.fileUrl))
            .map((file) => ({
                fileId: file.fileId,
                fileUrl: file.fileUrl,
                fileAccessUrl: file.fileAccessUrl,
                id: file.id,
            })) as IFileUploaderRes[];

        if (Helpers.isFunction(props?.onChange)) {
            props.onChange(files);
        }

        const totalFiles = fileList.length;
        const fullyUploaded = fileList.every((file) => !Helpers.isNullOrEmpty(file.fileId) || !Helpers.isNullOrEmpty(file.fileUrl));
        if (totalFiles > 0 && fullyUploaded && Helpers.isFunction(props?.finalOnChange)) {
            props.finalOnChange(files);
        }
    };

    const handleUploadDone = (renderKeyId: string, res: UploadFileResponse) => {
        for (let file of uploadingFiles.current) {
            if (file.renderKeyId === renderKeyId) {
                file.fileId = res.id;
                file.fileAccessUrl = res.accessUrl;
            }
        }

        const currentUploadings = uploadingFiles.current.filter((file) => Helpers.isNullOrEmpty(file.fileId));
        if (currentUploadings.length === 0) {
            let doneUploads: { [key: string]: IFileItem } = {};
            for (const file of uploadingFiles.current) {
                doneUploads[file.renderKeyId] = file;
            }

            for (let file of fileListRef.current) {
                const renderKeyId = file.renderKeyId;
                if (doneUploads[renderKeyId]) {
                    file.startUpload = false;
                    file.fileBinary = undefined;
                    file.fileId = doneUploads[renderKeyId].fileId;
                }
            }
            uploadingFiles.current = [];

            let uploads = 1;
            for (let file of fileListRef.current) {
                if (file.startUpload === false && !file.fileId && uploads <= uploadsPerTime) {
                    uploadingFiles.current.push(file);
                    file.renderKeyId = nanoid();
                    file.startUpload = true;
                    uploads += 1;
                }
            }

            propsOnChangeCallback();
            setFileList([...fileListRef.current]);
        }
    };

    const handleRemove = (renderKeyId: string) => {
        const currentUploads = uploadingFiles.current;
        let currentUploadsIndex: { [key: string]: number } = {};

        for (let index = 0; index < currentUploads.length; index++) {
            currentUploadsIndex[currentUploads[index].renderKeyId] = index;
        }

        for (let index = 0; index < fileListRef.current.length; index++) {
            if (fileListRef.current[index].renderKeyId === renderKeyId) {
                if (!Helpers.isNullOrEmpty(currentUploadsIndex[renderKeyId])) {
                    uploadingFiles.current.splice(currentUploadsIndex[renderKeyId], 1);
                }
                fileListRef.current.splice(index, 1);
                setFileList([...fileListRef.current]);
                propsOnChangeCallback();
                break;
            }
        }
    };

    const handleClickSingleChangeFile = (renderKeyId: string) => {
        if (uploadingFiles.current.length > 0) {
            return;
        }

        if (tabIndexRef.current === TabIndex.UploadFromLocal && singleChangeFileUploadRef.current) {
            changeFileRenderKeyId.current = renderKeyId;
            singleChangeFileUploadRef.current.value = null;
            singleChangeFileUploadRef.current.click();
        }
    };

    const handleSingleChangeFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            for (let file of fileListRef.current) {
                if (file.renderKeyId === changeFileRenderKeyId.current) {
                    file.renderKeyId = nanoid();
                    file.fileBinary = event.target.files[0];
                    file.startUpload = true;
                    file.fileId = undefined;
                    file.fileUrl = undefined;

                    uploadingFiles.current.push(file);
                    break;
                }
            }
            setFileList([...fileListRef.current]);
        }
    };

    return (
        <>
            {props.label && (
                <Typography
                    sx={{
                        fontSize: "0.75rem",
                        ...(!props.removeShadow && {
                            ml: "14px",
                        }),
                    }}
                    color={errorMessage ? "error" : "inherit"}
                >
                    {props.label} {props.required ? "*" : ""}
                </Typography>
            )}

            <Card
                style={{
                    ...(!props.removeShadow && {
                        padding: "8px 20px 20px 20px",
                    }),
                    width: "100%",
                    ...(props.removeShadow && { boxShadow: "unset" }),
                }}
            >
                <FileDropZone
                    mode={props.mode}
                    folderPath={props.folderPath}
                    optimize={props.optimize}
                    accessMode={props.accessMode}
                    key={TabIndex.UploadFromLocal.toString()}
                    multiple={props.multiple}
                    title={props.title}
                    disabled={
                        // Disable input when uploading files
                        fileList.filter((file) => file.startUpload === true).length > 0
                    }
                    onDropFiles={(newFiles: File[]) => handleAddFile(newFiles, undefined)}
                    fileListContainerRef={fileListContainerRef}
                    fileList={fileList}
                    handleClickSingleChangeFile={handleClickSingleChangeFile}
                    handleUploadDone={handleUploadDone}
                    handleRemove={handleRemove}
                    hiddenAction={props.hiddenAction}
                />

                {/* Single change file upload */}
                <input hidden type="file" multiple={false} ref={singleChangeFileUploadRef} onChange={handleSingleChangeFileUpload} />

                {/* <Typography sx={{ fontSize: "1rem", fontWeight: "bold", marginTop: "20px" }}>{`${strings.Common.TOTAL}: ${fileList.length}`}</Typography> */}
            </Card>
            {errorMessage && (
                <Box>
                    <FormHelperText
                        error
                        sx={{
                            ...(!props.removeShadow && {
                                ml: "14px",
                            }),
                        }}
                    >
                        {errorMessage}
                    </FormHelperText>
                </Box>
            )}
        </>
    );
};

export default MultipleImageUploader;
